/**
 * category-docs-editor.js
 */

import React from 'react';
import { itemTypes, CategoryFileList } from './models';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { usePreview } from 'react-dnd-preview';
import makeStyles from '@mui/styles/makeStyles';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TextField,
} from '@mui/material';
import { AppUtilityContext } from 'contexts/app-utility/app-utility-context';
import Button from 'components/button';
import ConfirmDialog from 'components/confirm-dialog';
import Dialog from 'components/dialog';
import Icon from 'components/icon';
import Document from './document';
import Category from './category';
import { isUsingTouchInput } from "utils/app-context";
import { randomString } from "utils/random";
import { MAX_REQUEST_SIZE_BYTES } from "utils/constants";


const useStyles = makeStyles(theme => ({
  subHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing(3),
    },
  },
  subHeaderText: {
    marginRight: theme.spacing(6),
    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing(3),
    },
  },
  subHeaderButton: {
    alignSelf: 'center',
    flexShrink: 0,
  },
  touchDragItem: {
    opacity: 0.8,
    backgroundColor: theme.palette.grey.colors[10],
    boxShadow: theme.shadows[7],
    maxWidth: `calc(100% - ${theme.spacing(6)})`,
  },
}));


export default function CategoryDocsEditor(props) {
  const {
    documents: docsProp,
    setDocFields,
  } = props;
  const classes = useStyles();
  const { alertWarning } = React.useContext(AppUtilityContext);
  const [documents, setDocuments] = React.useState();
  const [deletions, setDeletions] = React.useState();
  const [confirmDeleteFile, setConfirmDeleteFile] = React.useState();
  const [editNameOpen, setEditNameOpen] = React.useState(false);
  const [editNameObj, setEditNameObj] = React.useState({});


  React.useEffect(() => {
    if (docsProp) {
      setDocuments(docsProp.map(category => {
        const { files, ...otherProps } = category;
        const newCategory = new CategoryFileList({ ...otherProps });
        files.forEach(file => newCategory.addFile(file));
        return newCategory;
      }));
      
      // init deletions when docs props changes (open / save)
      setDeletions({
        files: [],
        categories: [],
      });
    }
  }, [docsProp]);


  const updateDocumentsDeletions = (docs, dels) => {
    setDocuments(docs);
    setDeletions(dels);
    setDocFields({
      documents: docs,
      deletions: dels,
    });
  };


  const TouchPreview = () => {
    const { display, item, style } = usePreview();
    if (!display || !isUsingTouchInput()) {
      return null;
    }
    return <Table
      size="small"
      style={style}
      className={classes.touchDragItem}
    >
      <TableBody>
        <Document
          file={item.file}
          style={{ borderBottom: 0 }}
          dragPreview
        />
      </TableBody>
    </Table>;
  }


  const addCategory = () => {
    setEditNameObj({
      name: '',
      type: itemTypes.CATEGORY,
      obj: { localId: randomString(8) },
      title: 'Create Category',
      create: true,
    });
    setEditNameOpen(true);
  }


  const editCategory = (category) => {
    setEditNameObj({
      name: category.name,
      type: itemTypes.CATEGORY,
      obj: category,
      title: 'Edit Category',
      create: false,
    });
    setEditNameOpen(true);
  }


  const editFilename = (file, category) => {
    setEditNameObj({
      name: file.filename.split('.').slice(0, -1).join('.'),
      type: itemTypes.FILE,
      obj: file,
      category,
      title: 'Edit Filename',
      create: false,
    });
    setEditNameOpen(true);
  }


  const saveName = () => {
    if (editNameObj.type === itemTypes.CATEGORY) {
      // validate -> no same category name
      if (documents.find(category => {
        return editNameObj.name.toLowerCase() === category.name.toLowerCase()
          && editNameObj.obj.localId !== category.localId;
      })) {
        setEditNameObj({ ...editNameObj, error: 'Category names must be unique' });
        return;
      } else if (editNameObj.name.length > 255) {
        setEditNameObj({ ...editNameObj, error: 'Category name max length is 255' });
        return;
      } else {
        if (editNameObj.create) {
          // create category
          updateDocumentsDeletions(
            [...documents, {
              name: editNameObj.name,
              localId: editNameObj.obj.localId,
              files: [],
            }],
            deletions
          );
        } else {
          // edit category
          updateDocumentsDeletions(
            documents.map(category => {
              return category.localId === editNameObj.obj.localId
                ? { ...editNameObj.obj, name: editNameObj.name }
                : category;
            }),
            deletions
          );
        }
      }
    } else if (editNameObj.type === itemTypes.FILE) {
      if (editNameObj.name.length > 496) {
        setEditNameObj({ ...editNameObj, error: 'File name max length is 500' });
        return;
      }
      const files = editNameObj.category.files.map(file => {
        return file.localId === editNameObj.obj.localId
          ? { ...editNameObj.obj, filename: `${editNameObj.name}.${editNameObj.obj.filename.split('.').pop()}` }
          : file;
      });
      updateDocumentsDeletions(
        documents.map(cat => {
          return cat.localId === editNameObj.category.localId
            ? { ...editNameObj.category, files }
            : cat;
        }),
        deletions
      );
    }

    setEditNameOpen(false);
    setTimeout(() => {
      setEditNameObj({});
    }, 500);
  }


  const deleteCategory = (category) => {
    let deletionsToUpdate = deletions;
    if (category.id) {
      deletionsToUpdate = {
        files: [...deletions.files],
        categories: [...deletions.categories, category.id]
      };
    }
    updateDocumentsDeletions(
      documents.filter(cat => cat.localId !== category.localId),
      deletionsToUpdate
    );
  };


  const onDrop = (item, type, category) => {
    if (item.files && item.files.length > 0) {
      // files dropped
      onFilesAdded(item.files, category);
    } else if (type && type === itemTypes.FILE) {
      // file row dropped
      onFileDragged(item.file, category);
    }
  }


  const onFileDragged = (file, category) => {
    let newdox = documents.map(cat => {
      if (cat.localId === file.category.localId) {
        // remove from category
        return { ...cat, files: cat.files.filter(f => f.localId !== file.localId) };
      } else if (cat.localId === category.localId) {
        // add to category
        let movedFile = { ...file };
        movedFile.category = category;
        return { ...cat, files: [...cat.files, movedFile] };
      } else {
        return cat;
      }
    });

    updateDocumentsDeletions(newdox, deletions);
  }


  const onFilesAdded = (files, category) => {
    // scan all files to check for oversize limit
    const size = calculateUploadSize(files);
    if (size > MAX_REQUEST_SIZE_BYTES) {
      alertWarning({
        title: 'Unable To Add File(s)',
        text: <>These file(s) exceed the max request limit of {MAX_REQUEST_SIZE_BYTES / 1024 / 1024} MB. Try saving first or adding fewer files.</>
      });
      return;
    }

    // check for duplicates
    const validFiles = [];
    const duplicates = [];
    files.forEach(f => {
      if (category.files.find(ef => ef.filename === f.name && ef.size === f.size)) {
        duplicates.push(f);
      } else {
        validFiles.push(f);
      }
    });
    if (duplicates.length > 0) {
      alertWarning({
        title: 'Duplicate Files Detected',
        text: <>
          {duplicates && 
            (duplicates?.length === 1
              ? `1 selected file appears to be a duplicate and was not added: `
              : `${duplicates?.length} selected files appear to be duplicates and were not added: `)
          }
          <br />
          {duplicates?.map(f => f.name).join(', ')}
        </>
      });
    }

    // generate new file list
    let newFiles = [...category.files];
    validFiles.forEach((file, i) => {
      newFiles.push({
        filename: file.name,
        localId: randomString(16),
        category,
        size: file.size,
        file,
      });
    });

    updateDocumentsDeletions(
      documents.map(cat => {
        return cat.localId === category.localId
          ? { ...category, files: newFiles }
          : cat;
      }),
      deletions
    );
  }


  const calculateUploadSize = (files) => {
    // start with existing docs
    let size = documents.reduce((sum, category) => {
      return category.files.reduce((sum, file) => {
        return !file.file
          ? sum
          : sum + file.file.size;
      }, sum);
    }, 0);

    // add new files
    size += files.reduce((sum, file) => {
      return sum + file.size;
    }, size);

    return size;
  }


  const deleteFile = async (file, category) => {
    if (await confirmDeleteFile()) {
      let deletionsToUpdate = deletions;
      if (file.id) {
        deletionsToUpdate = {
          files: [...deletions.files, file.id],
          categories: [...deletions.categories]
        };
      }
      updateDocumentsDeletions(
        documents.map(cat => {
          return cat.localId !== category.localId
            ? cat
            : {
              ...cat,
              files: cat.files.filter(f => f.localId !== file.localId)
            };
        }),
        deletionsToUpdate
      );
    }
  }


  return (
    <>
      {documents &&
      <DndProvider backend={isUsingTouchInput() ? TouchBackend : HTML5Backend}>
        <Box>
          <Box className={classes.subHeader} mb={1}>
            <Typography variant='subtext-large'>
              {isUsingTouchInput() 
                ? "Add files from your device to the categories below. " +
                  "Drag files between categories to organize."
                : "Drag and drop files from your computer into the categories below to upload. " +
                  "Drag files between categories to organize."}
            </Typography>
              <Button
                aria-label="add category"
                variant="outlined"
                size="small"
                onClick={addCategory}
                color="primary"
                className={classes.subHeaderButton}
              >
                <Icon type='add-circle' fontSize='small' />&nbsp;Category
              </Button>
          </Box>

          {documents.map((category, i) => (
            <Category
              key={category.localId}
              category={category}
              documents={documents}
              onFilesAdded={onFilesAdded}
              editCategory={editCategory}
              deleteCategory={deleteCategory}
              editFilename={editFilename}
              deleteFile={deleteFile}
              onDrop={(item, monitor) => onDrop(item, monitor.getItemType(), category)}
            />
          ))}
          <TouchPreview />

        </Box>

        {/* delete file confirm dialog */}
        <ConfirmDialog
          titleStyle="warning"
          title='Confirm Delete Document'
          titleIcon='warning'
          content="Are you sure you want to delete this document?"
          setConfirm={setConfirmDeleteFile}
        />

        {/* edit name dialog */}
        <Dialog
          maxWidth='xs'
          fullWidth
          title={editNameObj.title}
          titleIcon='edit'
          open={editNameOpen}
          onClose={() => setEditNameOpen(false)}
          onOk={saveName}
          autoFocusOkButton={false}
        >
          <TextField
            autoFocus
            fullWidth
            margin="normal"
            variant="outlined"
            placeholder={editNameObj.type === itemTypes.CATEGORY ? "Category Name" : "File Name"}
            value={editNameObj.name || ''}
            onChange={e => setEditNameObj({ ...editNameObj, name: e.target.value })}
            error={editNameObj.error !== undefined}
            helperText={editNameObj.error}
            onKeyDown={e => { if (e.key === "Enter") { setTimeout(() => saveName(), 100) } }}
          />
        </Dialog>

      </DndProvider>}
    </>
  );
}